import { createContext, useContext } from 'react';
import { ContactMethods } from './UserData';          // The user data context

// Define the type of the common order data
export type SessionDataType = {
  user_id?: string | null | undefined;
  telegram_user_id?: string | null | undefined;
  price_level?: number | string | null | undefined;
  channel_name?: string | null | undefined;             // usually is telegram bot name
  channel_first_name?: string | null | undefined;       // user's first name e.g. at telegram   
  channel_last_name?: string | null | undefined;        // user's last name e.g. at telegram   
  channel_user_name?: string | null | undefined;        // uniqueid username at the channel, e.g. at telegram
  channel?: string | null | undefined;
  lang?: string | null | undefined;
  loc_latitude?: number | null | undefined;
  loc_longitude?: number | null | undefined;
  location?: string | undefined;                        // e.g. "Montenegro_Bar"
  food_maker?: string | undefined;
  deliveryAddress?: string | undefined;
  entrance?: string | null | undefined;                 // A building entrance for a courier
  entrance_code?: string | null | undefined;            // An entrance code for a courier
  floor?: string | null | undefined;                    // A floor number for a courier
  apartment?: string | null | undefined;                // An apartment number for a courier
  delivery_instructions?: string | null | undefined;    // Additional instructions for a courier
  countryCode?: string | null | undefined;
  phone?: string | null | undefined;
  deliveryInTime?: string | undefined;                  // The value is either 'Yes' or 'No'
  time?: string | null | undefined;                     // e.g. "17?:00"
  date?: string | null | undefined;                     // e.g. "2024-04-03"
  deliveryInterval?: string | null | undefined;         // e.g. "14.00 - 16.00"
  deliveryNeeded?: string | undefined;                  // The value is either 'Yes' or 'No'
  changeNeeded?: string | undefined;                    // The value is either 'Yes' or 'No'
  changeAmount?: number | null | undefined;
  loyalty_card_number?: string | null | undefined;
  comment?: string | undefined;
  update_time?: number;
  contacts?: ContactMethods | undefined;
  ads?: string | undefined;                             // The ad code text for the order
};

const LOYALTY_CARD_DASH_VARIANTS_PATTERN = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/gu;
const LOYALTY_CARD_UNSUPPORTED_CHARACTERS_PATTERN = /[^\p{L}0-9\s-]/gu;

/**
 * Filters loyalty-card text while the customer is typing. Unicode letters,
 * ASCII digits, spaces, and hyphens are retained; a single outer space remains
 * possible so entering the next word does not fight controlled-input updates.
 */
export const sanitizeLoyaltyCardNumberInput = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(LOYALTY_CARD_DASH_VARIANTS_PATTERN, '-')
    .replace(LOYALTY_CARD_UNSUPPORTED_CHARACTERS_PATTERN, '')
    .replace(/\s+/gu, ' ')
    .replace(/-+/g, '-');
};

/**
 * Produces the canonical loyalty-card value used by review and submission.
 * It trims outer whitespace, removes spaces around hyphens, and collapses any
 * hyphen run created after separator cleanup without changing letter case.
 */
export const normalizeLoyaltyCardNumber = (value: unknown): string | null => {
  const normalizedValue = sanitizeLoyaltyCardNumberInput(value)
    .trim()
    .replace(/\s*-\s*/g, '-')
    .replace(/-+/g, '-');

  return normalizedValue.length > 0 ? normalizedValue : null;
};

// The context may be null before data is loaded

const SessionDataContext = createContext<SessionDataType | null>(null);

export const useSessionData = () => useContext(SessionDataContext);

export default SessionDataContext;
